import React, { useEffect, useMemo } from 'react';
import { Form, Select } from 'antd';

interface PrimaryCenterFieldProps {
  unitsList: any[];
  form: any;
  className?: string;
}

/**
 * Shared PrimaryCenterField for AddUser and EditUser forms.
 * Single-select dropdown whose options are restricted to the centers currently
 * selected in `user_unit_name`. The backend uses primary_center_code to resolve a
 * per-center DICOM proxy, so the primary must be one of the user's assigned centers.
 * When the selected primary is deselected from user_unit_name, it is cleared.
 */
const PrimaryCenterField: React.FC<PrimaryCenterFieldProps> = ({
  unitsList,
  form,
  className = '',
}) => {
  const selectedCenters = Form.useWatch('user_unit_name', form) || [];

  // Options are the assigned centers only, labelled from the full units list.
  const options = useMemo(() => {
    const labelByValue = new Map(unitsList.map(u => [u.value, u.label ?? u.value]));
    return selectedCenters.map(code => ({
      value: code,
      label: labelByValue.get(code) ?? code,
    }));
  }, [unitsList, selectedCenters]);

  // If the current primary was deselected from user_unit_name, reset it.
  useEffect(() => {
    const current = form.getFieldValue('primary_center_code');
    if (current && !selectedCenters.includes(current)) {
      form.setFieldsValue({ primary_center_code: undefined });
    }
  }, [selectedCenters, form]);

  return (
    <Form.Item
      className={className}
      label="Primary Center"
      name="primary_center_code"
      tooltip="Center used to resolve this user's DICOM proxy. Must be one of the selected work locations."
    >
      <Select
        placeholder="Select primary center"
        options={options}
        allowClear
        disabled={selectedCenters.length === 0}
      />
    </Form.Item>
  );
};

export default PrimaryCenterField;
